import React, { useState, useEffect } from 'react';

const TimerBarOverlay = ({ sessionId }) => {
  const [timerState, setTimerState] = useState(null);
  const [isFlashing, setIsFlashing] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    
    const mainWindow = window.opener || window.parent || window;
    
    const handleStateUpdate = (e) => {
      if (e.detail) {
        setTimerState(e.detail);
      }
    };
    
    mainWindow.addEventListener('presently-timer-update', handleStateUpdate);
    
    if (mainWindow.__presentlyTimerState) {
      setTimerState(mainWindow.__presentlyTimerState);
    }
    
    let unsubscribe = () => {};
    
    const initFirebaseSync = async () => {
      try {
        const { auth } = await import('../config/firebase');
        const { signInAnonymously } = await import('firebase/auth');
        const { timerService } = await import('../services/timerService');
        
        if (!auth.currentUser) {
          await signInAnonymously(auth);
        }
        
        unsubscribe = timerService.subscribeToTimer(sessionId, (data) => {
          setTimerState(data);
        });
      } catch (err) {
        console.warn('Firebase RTDB fallback connection failed:', err);
      }
    };
    
    initFirebaseSync();
    
    return () => {
      mainWindow.removeEventListener('presently-timer-update', handleStateUpdate);
      unsubscribe();
    };
  }, [sessionId]);

  // Flash indicator effect
  useEffect(() => {
    let flashInterval;
    if (!timerState) return;

    const minutesLeft = Math.ceil(timerState.timeRemaining / 60000);
    const shouldFlash = 
      (timerState.phase === 'preparation' && minutesLeft <= 2 && timerState.timeRemaining > 0) ||
      (timerState.phase === 'presentation' && timerState.timeRemaining === 0) ||
      (timerState.phase === 'q&a' && minutesLeft <= 1) ||
      (timerState.phase === 'overtime');

    if (shouldFlash) {
      flashInterval = setInterval(() => {
        setIsFlashing(prev => !prev);
      }, 500);
    } else {
      setIsFlashing(false);
    }

    return () => clearInterval(flashInterval);
  }, [timerState]);

  if (!timerState) {
    return (
      <div className="h-screen w-screen bg-slate-950 flex items-center justify-center text-slate-400 font-sans select-none border-b-4 border-rose-600">
        <div className="flex items-center space-x-3">
          <svg className="w-5 h-5 animate-spin text-rose-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-[10px] font-black uppercase tracking-[0.3em]">Connecting to Engine...</span>
        </div>
      </div>
    );
  }

  const formatTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(Math.abs(totalSeconds) / 60);
    const seconds = Math.floor(Math.abs(totalSeconds) % 60);
    const sign = totalSeconds < 0 ? '-' : '';
    return `${sign}${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatOvertime = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `-${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Color Mapping: Blue/Gray for Prep, Green/Yellow for Present, Purple/Orange for Q&A, and a Deep Red flashing animation state for Overtime
  const getOverlayStyles = () => {
    const minutesLeft = Math.ceil(timerState.timeRemaining / 60000);
    
    switch (timerState.phase) {
      case 'preparation':
        if (timerState.timeRemaining === 0) return 'bg-rose-950 text-rose-200 border-rose-900';
        if (minutesLeft <= 2) {
          return isFlashing ? 'bg-amber-500 text-black border-amber-400' : 'bg-slate-900 text-slate-100 border-slate-800';
        }
        return 'bg-slate-950 text-sky-400 border-slate-900';
        
      case 'presentation':
        if (timerState.timeRemaining === 0) {
          return isFlashing ? 'bg-rose-600 text-white border-rose-500' : 'bg-emerald-950 text-yellow-300 border-emerald-900';
        }
        if (minutesLeft <= 2 || minutesLeft <= 5) return 'bg-amber-500 text-black border-amber-400';
        return 'bg-emerald-950 text-yellow-300 border-emerald-900';
        
      case 'q&a':
        if (timerState.timeRemaining === 0) return 'bg-rose-900 text-white border-rose-800 animate-pulse';
        if (minutesLeft <= 1) {
          return isFlashing ? 'bg-rose-600 text-white border-rose-500' : 'bg-purple-950 text-orange-400 border-purple-900';
        }
        return 'bg-purple-950 text-orange-400 border-purple-900';
        
      case 'overtime':
        const pulseClass = Math.floor((timerState.overtimeSeconds || 0) / 2) % 2 === 0 ? 'bg-red-950 text-red-200' : 'bg-black text-red-500';
        return `${pulseClass} border-red-900 animate-pulse`;
        
      default:
        return 'bg-slate-950 text-white border-slate-900';
    }
  };

  const formattedTime = timerState.phase === 'overtime' 
    ? formatOvertime(timerState.overtimeSeconds) 
    : formatTime(timerState.timeRemaining);

  const phaseLabel = timerState.phase === 'overtime' ? 'overtime' : `${timerState.phase} phase`;

  return (
    <div className={`h-screen w-screen flex items-center justify-between px-8 select-none font-sans overflow-hidden border-b-4 transition-all duration-500 ${getOverlayStyles()}`}>
      <div className="flex items-center space-x-3">
        <span className={`w-2 h-2 rounded-full ${timerState.isRunning ? 'bg-green-500 animate-ping' : 'bg-rose-500'}`} />
        <span className="text-[10px] font-black uppercase tracking-[0.2em]">{phaseLabel}</span>
      </div>
      <div className="text-4xl font-black tabular-nums tracking-tighter drop-shadow-md">
        {formattedTime}
      </div>
    </div>
  );
};

export default TimerBarOverlay;
